import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Key, Plus, RefreshCw, AlertCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useAccount } from '@/contexts/AccountContext'
import { listApiKeys } from '../api/apiKeys'
import { ApiKeyTable } from '../components/ApiKeyTable'
import { CreateApiKeyModal } from '../components/CreateApiKeyModal'

export function ApiKeyManagementPage() {
  const { selectedAccountId } = useAccount()
  const [showCreate, setShowCreate] = useState(false)
  const [enabledOnly, setEnabledOnly] = useState(false)

  const accountId = selectedAccountId && selectedAccountId !== 'ALL' ? selectedAccountId : null

  const { data: keys = [], isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey: ['api-keys', accountId, enabledOnly],
    queryFn: () => listApiKeys(accountId!, enabledOnly),
    enabled: !!accountId,
  })

  // ── No account selected ───────────────────────────────────────────────────
  if (!accountId) {
    return (
      <div className="p-6">
        <PageHeader />
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-amber-100 mx-auto mb-4">
            <AlertCircle className="h-7 w-7 text-amber-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900">Select an Account</h3>
          <p className="text-sm text-gray-500 mt-1">
            Use the account filter in the top bar to select an account before managing API keys.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="p-6">
      <div className="flex items-start justify-between mb-6">
        <PageHeader />
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={enabledOnly}
              onChange={(e) => setEnabledOnly(e.target.checked)}
              className="rounded border-gray-300 accent-primary"
            />
            Active only
          </label>
          <Button
            variant="outline"
            size="sm"
            onClick={() => refetch()}
            disabled={isFetching}
          >
            <RefreshCw className={`h-4 w-4 mr-1.5 ${isFetching ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button size="sm" onClick={() => setShowCreate(true)}>
            <Plus className="h-4 w-4 mr-1.5" />
            Create API Key
          </Button>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200">
        {/* Account context bar */}
        <div className="flex items-center gap-2 px-5 py-3 border-b bg-gray-50 rounded-t-xl">
          <Key className="h-4 w-4 text-gray-400" />
          <span className="text-sm text-gray-600">
            Account <span className="font-semibold text-gray-900">{accountId}</span>
            {!isLoading && (
              <span className="text-gray-400 ml-2">— {keys.length} key{keys.length !== 1 ? 's' : ''}</span>
            )}
          </span>
        </div>

        {/* Content */}
        <div className="p-2">
          {isLoading ? (
            <div className="py-16 text-center text-gray-500 text-sm">Loading API keys…</div>
          ) : isError ? (
            <div className="py-12 text-center">
              <p className="text-sm text-red-600 font-medium">Failed to load API keys</p>
              <p className="text-xs text-gray-400 mt-1">{(error as Error).message}</p>
              <Button variant="outline" size="sm" className="mt-4" onClick={() => refetch()}>
                Retry
              </Button>
            </div>
          ) : (
            <ApiKeyTable accountId={accountId} keys={keys} />
          )}
        </div>
      </div>

      {showCreate && (
        <CreateApiKeyModal
          accountId={accountId}
          onClose={() => setShowCreate(false)}
        />
      )}
    </div>
  )
}

function PageHeader() {
  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-900">API Key Management</h1>
      <p className="text-gray-500 text-sm mt-1">
        Create and manage API keys for M2M (machine-to-machine) authentication
      </p>
    </div>
  )
}
